import {
  DestroyRef,
  Injectable,
  NgZone,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { DeviceService } from './device.service';

const OFFER_ALERT_INTERVAL_MS = 4000;
const OFFER_ALERT_FREQUENCY_HZ = 880;
const OFFER_ALERT_BEEP_SECONDS = 0.18;
const OFFER_ALERT_BEEP_GAP_SECONDS = 0.12;
const OFFER_ALERT_GAIN = 0.12;
const OFFER_ALERT_RAMP_SECONDS = 0.02;
const SILENT_GAIN = 0.0001;
const OFFER_ALERT_UNLOCK_EVENTS: readonly string[] = ['pointerdown', 'keydown'];

type SinkCapableAudioElement = HTMLAudioElement & {
  setSinkId?: (sinkId: string) => Promise<void>;
};

/**
 * Ringtone service using Web Audio API.
 * Plays a three-tone burst pattern for incoming calls.
 */
@Injectable({ providedIn: 'root' })
export class RingtoneService {
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly deviceService = inject(DeviceService);
  private readonly ringtoneSource = 'assets/audio/ringtone.mp3';

  private audioContext: AudioContext | null = null;
  private ringtoneAudio: HTMLAudioElement | null = null;
  private ringbackAudio: HTMLAudioElement | null = null;
  private ringbackDestination: MediaStreamAudioDestinationNode | null = null;
  private ringbackIntervalId: ReturnType<typeof setInterval> | null = null;
  private ringbackStopTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private ringbackGainNode: GainNode | null = null;
  private ringbackOscillators: OscillatorNode[] = [];

  // Callback offer alarm (K-172): own interval, oscillator and <audio>; never the auxiliary device slot.
  private readonly document = inject(DOCUMENT);
  private offerAlertAudio: HTMLAudioElement | null = null;
  private offerAlertDestination: MediaStreamAudioDestinationNode | null = null;
  private offerAlertIntervalId: ReturnType<typeof setInterval> | null = null;
  private offerAlertStopTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private offerAlertGainNode: GainNode | null = null;
  private offerAlertOscillator: OscillatorNode | null = null;
  private disarmOfferAlertUnlock: (() => void) | null = null;

  readonly muted = signal(false);

  constructor() {
    // F2-06: DeviceService routes only the active/auxiliary elements; the offer alarm follows the selection itself.
    effect(() => {
      const outputId = this.deviceService.getSelectedAudioOutputId();
      untracked(() => this.routeOfferAlert(outputId));
    });

    this.destroyRef.onDestroy(() => {
      this.stop();
      this.stopRingback();
      this.stopOfferAlert();

      if (this.offerAlertAudio) {
        this.offerAlertAudio.srcObject = null;
        this.offerAlertAudio = null;
      }
      this.offerAlertDestination = null;

      if (this.ringtoneAudio) {
        this.deviceService.setAuxiliaryAudioElement(null);
        this.ringtoneAudio.pause();
        this.ringtoneAudio.src = '';
        this.ringtoneAudio.load();
        this.ringtoneAudio = null;
      }

      if (this.ringbackAudio) {
        this.ringbackAudio.pause();
        this.ringbackAudio.srcObject = null;
        this.ringbackAudio.load();
        this.ringbackAudio = null;
      }
      this.ringbackDestination = null;

      if (this.audioContext) {
        void this.audioContext.close().catch(() => {
          // Best effort cleanup.
        });
        this.audioContext = null;
      }
    });
  }

  start(): void {
    if (this.muted()) {
      return;
    }

    // Incoming ringtone and ringback are mutually exclusive.
    this.stopRingback();
    this.ensureRingtoneElement();
    if (!this.ringtoneAudio || !this.ringtoneAudio.paused) {
      return;
    }

    this.deviceService.setAuxiliaryAudioElement(this.ringtoneAudio);
    this.ringtoneAudio.currentTime = 0;
    void this.ringtoneAudio.play().catch(() => {
      // Playback can be blocked by browser autoplay policy.
    });
  }

  stop(): void {
    if (!this.ringtoneAudio) {
      return;
    }

    this.ringtoneAudio.pause();
    this.ringtoneAudio.currentTime = 0;
    this.deviceService.setAuxiliaryAudioElement(null);
  }

  startRingback(): void {
    if (this.ringbackIntervalId) {
      return;
    }

    // Incoming ringtone and ringback are mutually exclusive.
    this.stop();
    this.ensureAudioContext();
    this.ensureRingbackOutputRoute();
    this.playRingbackCycle();

    this.zone.runOutsideAngular(() => {
      this.ringbackIntervalId = setInterval(() => {
        this.playRingbackCycle();
      }, 4000);
    });
  }

  stopRingback(): void {
    if (this.ringbackIntervalId) {
      clearInterval(this.ringbackIntervalId);
      this.ringbackIntervalId = null;
    }

    if (this.ringbackStopTimeoutId) {
      clearTimeout(this.ringbackStopTimeoutId);
      this.ringbackStopTimeoutId = null;
    }

    this.stopActiveRingbackTone();
    this.pauseRingbackOutputRoute();
  }

  toggleMute(isRinging: boolean): void {
    const wasMuted = this.muted();
    this.muted.set(!wasMuted);

    if (isRinging) {
      if (!wasMuted) {
        // Muting - stop ringtone
        this.stop();
      } else {
        // Unmuting - restart ringtone
        this.start();
      }
    }
  }

  resetMute(): void {
    this.muted.set(false);
  }

  /** Repeating double beep while a callback is offered (154880/6.1); `stop`, `stopRingback` and `muted` leave it alone. */
  startOfferAlert(): void {
    if (this.offerAlertIntervalId !== null) {
      return;
    }

    this.ensureAudioContext();
    this.ensureOfferAlertOutputRoute();
    this.playOfferAlertCycle();

    this.zone.runOutsideAngular(() => {
      this.offerAlertIntervalId = setInterval(() => {
        this.playOfferAlertCycle();
      }, OFFER_ALERT_INTERVAL_MS);
    });

    if (this.audioContext?.state === 'suspended') {
      this.armOfferAlertUnlock();
    }
  }

  stopOfferAlert(): void {
    if (this.offerAlertIntervalId !== null) {
      clearInterval(this.offerAlertIntervalId);
      this.offerAlertIntervalId = null;
    }

    this.stopActiveOfferAlertTone();
    this.disarmOfferAlertUnlock?.();
    this.offerAlertAudio?.pause();
  }

  private ensureAudioContext(): void {
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
    }

    if (this.audioContext.state === 'suspended') {
      void this.audioContext.resume().catch(() => {
        // Resume may fail until user gesture.
      });
    }
  }

  private ensureRingtoneElement(): void {
    if (this.ringtoneAudio) {
      return;
    }

    const audio = new Audio(this.ringtoneSource);
    audio.preload = 'auto';
    audio.loop = true;
    this.ringtoneAudio = audio;
  }

  private playRingbackCycle(): void {
    if (!this.audioContext || this.ringbackOscillators.length > 0) {
      return;
    }

    const ctx = this.audioContext;
    const now = ctx.currentTime;
    const toneDurationSeconds = 1.5;

    const gain = ctx.createGain();
    const primary = ctx.createOscillator();
    const secondary = ctx.createOscillator();

    primary.type = 'sine';
    primary.frequency.value = 440;
    secondary.type = 'sine';
    secondary.frequency.value = 480;

    primary.connect(gain);
    secondary.connect(gain);
    gain.connect(this.ringbackDestination ?? ctx.destination);

    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.12, now + 0.02);
    gain.gain.setValueAtTime(0.12, now + toneDurationSeconds - 0.05);
    gain.gain.exponentialRampToValueAtTime(
      0.0001,
      now + toneDurationSeconds
    );

    primary.start(now);
    secondary.start(now);
    primary.stop(now + toneDurationSeconds);
    secondary.stop(now + toneDurationSeconds);

    this.ringbackGainNode = gain;
    this.ringbackOscillators = [primary, secondary];

    this.zone.runOutsideAngular(() => {
      this.ringbackStopTimeoutId = setTimeout(() => {
        this.stopActiveRingbackTone();
      }, (toneDurationSeconds + 0.1) * 1000);
    });
  }

  private stopActiveRingbackTone(): void {
    if (this.ringbackStopTimeoutId) {
      clearTimeout(this.ringbackStopTimeoutId);
      this.ringbackStopTimeoutId = null;
    }

    this.ringbackOscillators.forEach(osc => {
      try {
        osc.stop();
      } catch {
        // Oscillator might already be stopped.
      }
      osc.disconnect();
    });
    this.ringbackOscillators = [];

    if (this.ringbackGainNode) {
      this.ringbackGainNode.disconnect();
      this.ringbackGainNode = null;
    }
  }

  private ensureRingbackOutputRoute(): void {
    if (!this.audioContext) {
      return;
    }

    if (!this.ringbackDestination) {
      this.ringbackDestination = this.audioContext.createMediaStreamDestination();
    }

    if (!this.ringbackAudio) {
      const audio = new Audio();
      audio.preload = 'auto';
      audio.srcObject = this.ringbackDestination.stream;
      this.ringbackAudio = audio;
    } else if (!this.ringbackAudio.srcObject) {
      this.ringbackAudio.srcObject = this.ringbackDestination.stream;
    }

    this.deviceService.setAuxiliaryAudioElement(this.ringbackAudio);
    void this.ringbackAudio.play().catch(() => {
      // Playback can be blocked by browser autoplay policy.
    });
  }

  private pauseRingbackOutputRoute(): void {
    this.deviceService.setAuxiliaryAudioElement(null);

    if (!this.ringbackAudio) {
      return;
    }

    this.ringbackAudio.pause();
  }

  private playOfferAlertCycle(): void {
    if (!this.audioContext || this.offerAlertOscillator) {
      return;
    }

    const ctx = this.audioContext;
    const now = ctx.currentTime;
    const secondBeepAt = now + OFFER_ALERT_BEEP_SECONDS + OFFER_ALERT_BEEP_GAP_SECONDS;
    const endAt = secondBeepAt + OFFER_ALERT_BEEP_SECONDS;

    const gain = ctx.createGain();
    const oscillator = ctx.createOscillator();
    oscillator.type = 'sine';
    oscillator.frequency.value = OFFER_ALERT_FREQUENCY_HZ;
    oscillator.connect(gain);
    gain.connect(this.offerAlertDestination ?? ctx.destination);

    [now, secondBeepAt].forEach(beepAt => {
      gain.gain.setValueAtTime(SILENT_GAIN, beepAt);
      gain.gain.exponentialRampToValueAtTime(OFFER_ALERT_GAIN, beepAt + OFFER_ALERT_RAMP_SECONDS);
      gain.gain.setValueAtTime(OFFER_ALERT_GAIN, beepAt + OFFER_ALERT_BEEP_SECONDS - OFFER_ALERT_RAMP_SECONDS);
      gain.gain.exponentialRampToValueAtTime(SILENT_GAIN, beepAt + OFFER_ALERT_BEEP_SECONDS);
    });

    oscillator.start(now);
    oscillator.stop(endAt);
    this.offerAlertGainNode = gain;
    this.offerAlertOscillator = oscillator;

    this.zone.runOutsideAngular(() => {
      this.offerAlertStopTimeoutId = setTimeout(() => {
        this.stopActiveOfferAlertTone();
      }, (endAt - now) * 1000);
    });
  }

  private stopActiveOfferAlertTone(): void {
    if (this.offerAlertStopTimeoutId !== null) {
      clearTimeout(this.offerAlertStopTimeoutId);
      this.offerAlertStopTimeoutId = null;
    }

    if (this.offerAlertOscillator) {
      try {
        this.offerAlertOscillator.stop();
      } catch {
        // Oscillator might already be stopped.
      }
      this.offerAlertOscillator.disconnect();
      this.offerAlertOscillator = null;
    }

    this.offerAlertGainNode?.disconnect();
    this.offerAlertGainNode = null;
  }

  private ensureOfferAlertOutputRoute(): void {
    if (!this.audioContext) {
      return;
    }

    this.offerAlertDestination ??= this.audioContext.createMediaStreamDestination();
    if (!this.offerAlertAudio) {
      const audio = new Audio();
      audio.srcObject = this.offerAlertDestination.stream;
      this.offerAlertAudio = audio;
    }

    this.routeOfferAlert(this.deviceService.getSelectedAudioOutputId());
    void this.offerAlertAudio.play().catch(() => this.armOfferAlertUnlock());
  }

  private routeOfferAlert(outputId: string | null): void {
    const audio: SinkCapableAudioElement | null = this.offerAlertAudio;
    if (audio && outputId && typeof audio.setSinkId === 'function') {
      void audio.setSinkId(outputId).catch(() => {
        // Falls back to the default output device.
      });
    }
  }

  /** Autoplay lock (e.g. offer right after F5): resume on the agent's first click or key press. */
  private armOfferAlertUnlock(): void {
    if (this.disarmOfferAlertUnlock) {
      return;
    }

    const unlock = (): void => this.unlockOfferAlert();
    this.zone.runOutsideAngular(() => {
      OFFER_ALERT_UNLOCK_EVENTS.forEach(type =>
        this.document.addEventListener(type, unlock, { once: true }),
      );
    });

    this.disarmOfferAlertUnlock = () => {
      OFFER_ALERT_UNLOCK_EVENTS.forEach(type => this.document.removeEventListener(type, unlock));
      this.disarmOfferAlertUnlock = null;
    };
  }

  private unlockOfferAlert(): void {
    this.disarmOfferAlertUnlock?.();
    if (this.offerAlertIntervalId === null) {
      return;
    }

    void this.audioContext?.resume().catch(() => this.armOfferAlertUnlock());
    void this.offerAlertAudio?.play().catch(() => this.armOfferAlertUnlock());
    this.stopActiveOfferAlertTone();
    this.playOfferAlertCycle();
  }
}
